
import React from "react";
import { BarChart, ResponsiveContainer, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Sun, ArrowDown, ArrowUp, BarChart as BarChartIcon } from "lucide-react";
import { DadosIrradiacao } from "@/types";

interface IrradiationDataDisplayProps {
  dadosIrradiacao: DadosIrradiacao;
}

export const IrradiationDataDisplay = ({ dadosIrradiacao }: IrradiationDataDisplayProps) => {
  const dadosGrafico = dadosIrradiacao?.dadosMensais?.map(item => ({
    name: item.mes.substring(0, 3),
    irradiacao: item.valor
  })) || [];

  const chartConfig = {
    solar: {
      label: "Irradiação Solar",
      color: "#16a34a"
    }
  };

  return (
    <div className="mt-6 space-y-5 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
            <Sun className="h-4 w-4 text-amber-500" />
            Irradiação Média
          </p>
          <p className="text-2xl font-bold text-green-700">{dadosIrradiacao.irradiacaoMedia.toFixed(2)} kWh/m²/dia</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
            <ArrowDown className="h-4 w-4 text-blue-500" />
            Mês com Menor Irradiação
          </p>
          <p className="text-xl font-bold text-blue-700">{dadosIrradiacao.mesMin} ({dadosIrradiacao.minIrradiacao.toFixed(2)})</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
            <ArrowUp className="h-4 w-4 text-amber-500" />
            Mês com Maior Irradiação
          </p>
          <p className="text-xl font-bold text-amber-700">{dadosIrradiacao.mesMax} ({dadosIrradiacao.maxIrradiacao.toFixed(2)})</p>
        </div>
      </div>
      
      <div className="mt-4 border rounded-lg p-4 bg-white shadow-sm">
        <h3 className="text-base font-semibold mb-2 flex items-center gap-2 text-green-700">
          <BarChartIcon className="h-4 w-4 text-green-600" /> 
          Irradiação Solar Mensal (kWh/m²/dia)
        </h3>
        <div className="h-[180px] w-full">
          <ChartContainer config={chartConfig}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={dadosGrafico}
                margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 9 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  domain={[0, 'auto']} 
                  tick={{ fontSize: 9 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => value.toFixed(1)}
                  width={24}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent />
                  }
                />
                <Bar 
                  dataKey="irradiacao" 
                  fill="var(--color-solar)" 
                  name="solar" 
                  radius={[3, 3, 0, 0]} 
                  animationDuration={1500}
                  barSize={12}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      </div>
    </div>
  );
};
